import React from 'react';
import { Box, Container, Typography, Grid, Card, CardMedia, CardContent, Button, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ProjectCard = ({ project, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: isMobile ? 0 : index * 0.2 }}
    >
      <Box
        sx={{
          height: '100%',
          borderRadius: { xs: 3, md: 4 },
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: { xs: 'translateY(-5px)', md: 'translateY(-10px)' },
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: theme.palette.primary.main,
            '& .project-image': {
              transform: 'scale(1.05)',
            },
          },
        }}
      >
        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
          <CardMedia
            component="img"
            height={{ xs: '200', sm: '250', md: '300' }}
            image={project.img}
            alt={project.title}
            className="project-image"
            sx={{
              transition: 'transform 0.5s ease-in-out',
            }}
          />
        </Box>
        <Box sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
          <Typography 
            variant="h5" 
            component="h3" 
            sx={{ 
              mb: { xs: 1.5, md: 2 },
              color: 'rgba(180, 180, 180, 1)',
              fontWeight: 600,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              lineHeight: 1.3,
            }}
          >
            {project.title}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: { xs: 2, md: 3 },
              color: 'rgba(160, 160, 160, .8)',
              fontSize: { xs: '0.9rem', md: '1rem' },
              lineHeight: 1.6,
            }}
          >
            {project.description}
          </Typography>
          {project.link && (
            <Button
              variant="contained"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                boxShadow: '0 8px 16px rgba(255, 107, 0, 0.2)',
                py: { xs: 1, md: 1.5 },
                px: { xs: 2.5, md: 3 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                textTransform: 'none',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 20px rgba(255, 107, 0, 0.3)',
                },
              }}
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn More
            </Button>
          )}
        </Box>
      </Box>
    </motion.div>
  );
};

const Projects = ({ data }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));

  // Determine columns based on screen size
  const getColumns = () => {
    if (isXs) return 12; // 1 column on mobile
    if (isSm) return 6;  // 2 columns on tablet
    if (isMd) return 6;  // 2 columns on medium screens
    return 6;            // 2 columns on large screens
  };

  return (
    <Box
      id="projects"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#121212',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("/Nuyu-media/Nuyu Pattern_1.png")',
          backgroundRepeat: 'repeat',
          opacity: 0.05,
          zIndex: 1,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{ 
              mb: { xs: 4, md: 8 },
              color: 'rgba(200, 200, 200, 1)',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Our Projects
          </Typography>
        </motion.div>

        <Grid 
          container 
          spacing={{ xs: 2.5, sm: 3, md: 4 }}
          sx={{
            width: '100%',
            margin: '0 auto',
          }}
        >
          {data.map((project, index) => (
            <Grid item xs={getColumns()} key={index}>
              <ProjectCard project={project} index={index} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Projects;
